/**
 * Shared, lightly polled agent queue (nav badge + "New from Lighthouse" list, Home "New messages" card).
 * One poller for the whole app regardless of how many components read it.
 *
 * On native the poll interval stops while the app is in the background, so the app also refreshes the queue
 * the moment it resumes and when a push arrives in the foreground (`refreshAgentQueue`, wired in
 * src/native/platform.ts and nativePushActions.ts).
 */
import { computed, ref } from 'vue'
import { agentApi, type QueueItem } from './api'
import { isSupportViewLimited } from '@/utils/supportView'

const counts = ref({ needs_you: 0, fyi: 0, unread: 0 })
const items = ref<QueueItem[]>([])
const loaded = ref(false)
let timer: ReturnType<typeof setInterval> | null = null
let users = 0
/** While a tour or guest workspace has seeded the queue, the server is not asked. */
let seeded = false

async function refresh(): Promise<void> {
    if (seeded) return
    // Support view: the queue is the agent's email-derived messages; show it empty instead of asking.
    if (isSupportViewLimited()) {
        items.value = []
        counts.value = { needs_you: 0, fyi: 0, unread: 0 }
        loaded.value = true
        return
    }
    try {
        const q = await agentApi().getQueue()
        counts.value = q.counts
        items.value = q.items
    } catch {
        /* keep the last value */
    } finally {
        loaded.value = true
    }
}

/** Refresh now (app resumed, a push arrived). Coalesces with an in-flight refresh. */
let inflight: Promise<void> | null = null
export function refreshAgentQueue(): Promise<void> {
    if (!inflight) inflight = refresh().finally(() => { inflight = null })
    return inflight
}

/** The /dashboard/shell aggregate carries the same queue payload; use it instead of a second request. */
export function hydrateAgentQueue(q: { items?: QueueItem[]; counts?: { needs_you: number; fyi: number; unread: number } } | null | undefined): void {
    if (seeded || !q) return
    if (isSupportViewLimited()) q = { items: [] }
    items.value = q.items ?? []
    counts.value = q.counts ?? {
        needs_you: items.value.filter((i) => i.kind === 'needs_you').length,
        fyi: items.value.filter((i) => i.kind === 'fyi').length,
        unread: items.value.filter((i) => i.unread).length,
    }
    loaded.value = true
}

/** Guest mode and tours seed the queue without a backend. */
export function seedAgentQueue(next: QueueItem[]): void {
    seeded = true
    items.value = next
    counts.value = {
        needs_you: next.filter((i) => i.kind === 'needs_you').length,
        fyi: next.filter((i) => i.kind === 'fyi').length,
        unread: next.filter((i) => i.unread).length,
    }
    loaded.value = true
}

/** Drop a seeded (demo) queue and go back to the server. */
export function clearAgentQueueSeed(): void {
    if (!seeded) return
    seeded = false
    items.value = []
    counts.value = { needs_you: 0, fyi: 0, unread: 0 }
    loaded.value = false
    if (users) void refresh()
}

/** True while the queue shows seeded demo items. */
export function agentQueueSeeded(): boolean {
    return seeded
}

export default function useAgentQueueCounts(pollMs = 30_000) {
    function start() {
        users += 1
        if (!timer) {
            void refresh()
            timer = setInterval(refresh, pollMs)
        }
    }
    function stop() {
        users = Math.max(0, users - 1)
        if (!users && timer) {
            clearInterval(timer)
            timer = null
        }
    }
    const needsYouItems = computed(() => items.value.filter((i) => i.kind === 'needs_you'))
    return {
        counts,
        items,
        loaded,
        needsYouItems,
        refresh,
        start,
        stop,
        needsYou: computed(() => counts.value.needs_you),
        unread: computed(() => counts.value.unread),
        urgentCount: computed(() => needsYouItems.value.filter((i) => i.urgency === 'today').length),
    }
}
